import React, { useState, useEffect, useRef } from "react";
import { Phone, Menu, X, Wrench, ChevronDown } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "./ui/button";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "./ui/navigation-menu";
import { COMPANY, NAV_MENU } from "../mock";
import { trackPhoneClick } from "../lib/analytics";
import EmergencyBanner from "./EmergencyBanner";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const bannerRef = useRef(null);
  const [stickyOffset, setStickyOffset] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState({}); // index -> bool
  const location = useLocation();
  const isHome = location.pathname === "/";

  // Pin the header directly beneath the sticky emergency banner.
  useEffect(() => {
    const el = bannerRef.current;
    if (!el) return;
    const update = () => setStickyOffset(el.offsetHeight);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Hash links should stay on home page; on inner pages we send users back to
  // home and scroll to the anchor.
  const resolveHref = (href) => {
    if (href.includes("#") && !isHome) return href; // /#services from /samsung
    if (href.includes("#") && isHome) return href.replace(/^\//, "") || "/";
    return href;
  };

  const linkClass =
    "text-[12.5px] 2xl:text-[13.5px] font-bold uppercase tracking-wide text-slate-700 hover:text-orange-700 transition-colors whitespace-nowrap";

  const toggleMobileSection = (idx) =>
    setMobileExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const closeMobile = () => {
    setMobileOpen(false);
    setMobileExpanded({});
  };

  return (
    <>
      <EmergencyBanner ref={bannerRef} />
      {/* Top utility bar */}
      <div className="hidden md:block bg-slate-900 text-slate-200 text-xs">
        <div className="max-w-[1400px] mx-auto px-6 h-9 flex items-center justify-between">
          <span className="text-slate-300 uppercase tracking-wider">
            {COMPANY.hours} &nbsp;·&nbsp; Serving DeKalb County, GA since 2007 &nbsp;·&nbsp;{" "}
            <Link to="/careers" data-testid="utility-careers-link" className="text-orange-400 hover:text-orange-300 font-bold normal-case tracking-normal">Technician Careers →</Link>
          </span>
          <a
            href={`tel:${COMPANY.phoneRaw}`}
            data-testid="utility-phone-link"
            onClick={() => trackPhoneClick("utility-bar")}
            className="flex items-center gap-2 hover:text-orange-400 transition-colors font-semibold"
          >
            <Phone className="h-3 w-3" aria-hidden="true" />
            {COMPANY.phone}
          </a>
        </div>
      </div>

      <header
        style={{ top: stickyOffset }}
        className={`sticky z-50 w-full transition-[background-color,box-shadow] duration-300 ${
          scrolled ? "bg-white shadow-md" : "bg-white"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 h-20 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link
            to="/"
            data-testid="header-logo"
            className="flex items-center gap-3 group flex-shrink-0"
          >
            <div className="h-11 w-11 rounded-xl bg-orange-500 flex items-center justify-center shadow group-hover:scale-105 transition-transform">
              <Wrench className="h-6 w-6 text-white" strokeWidth={2.5} aria-hidden="true" />
            </div>
            <div className="leading-tight">
              <div
                className="text-[16px] sm:text-[18px] font-extrabold tracking-tight"
                style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 800 }}
              >
                <span className="text-slate-900">DECATUR</span>{" "}
                <span className="text-orange-700">SOLUTION</span>{" "}
                <span className="text-slate-900">WORKS</span>
              </div>
              <div className="text-[9.5px] uppercase tracking-[0.2em] text-slate-500 font-semibold">
                Appliance Repair
              </div>
            </div>
            <img
              src="/site-images/google-guaranteed-badge.png"
              alt="Google Guaranteed Service Provider - Decatur Solution Works verified by Google"
              width={44}
              height={44}
              loading="eager"
              decoding="async"
              className="h-11 w-11 object-contain flex-shrink-0 hidden sm:block xl:hidden 2xl:block"
            />
          </Link>

          {/* Desktop nav with dropdowns */}
          <NavigationMenu className="hidden xl:flex" data-testid="desktop-nav">
            <NavigationMenuList className="gap-1">
              {NAV_MENU.map((item, idx) => {
                if (item.type === "link") {
                  return (
                    <NavigationMenuItem key={item.label}>
                      <Link
                        to={item.href}
                        data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className={`${linkClass} px-2.5 py-2 inline-block`}
                      >
                        {item.label}
                      </Link>
                    </NavigationMenuItem>
                  );
                }
                return (
                  <NavigationMenuItem key={item.label}>
                    <NavigationMenuTrigger
                      data-testid={`nav-trigger-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="text-[12.5px] 2xl:text-[13.5px] px-2.5 font-bold uppercase tracking-wide text-slate-700 hover:text-orange-700 data-[state=open]:text-orange-700 bg-transparent hover:bg-orange-50/60 data-[state=open]:bg-orange-50/60"
                    >
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul
                        data-testid={`nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="grid w-[520px] gap-1 p-3 sm:grid-cols-1 max-h-[70vh] overflow-y-auto"
                      >
                        {item.items.map((sub) => (
                          <li key={sub.href}>
                            <NavigationMenuLink asChild>
                              <Link
                                to={sub.href}
                                data-testid={`nav-item-${sub.href.replace(/[^a-z0-9]+/gi, "-")}`}
                                className="block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-orange-50 hover:text-orange-800 focus:bg-orange-50 focus:text-orange-800"
                              >
                                <div className="text-sm font-bold text-slate-900 group-hover:text-orange-800">
                                  {sub.label}
                                </div>
                                {sub.desc && (
                                  <p className="line-clamp-2 text-xs leading-snug text-slate-500 mt-1">
                                    {sub.desc}
                                  </p>
                                )}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                );
              })}
              <NavigationMenuItem>
                <Link
                  to="/contact"
                  data-testid="nav-link-contact"
                  className={`${linkClass} px-2.5 py-2 inline-block`}
                >
                  Contact Us
                </Link>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>

          {/* Desktop call CTA */}
          <div className="hidden xl:flex items-center gap-3 flex-shrink-0">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="header-call-cta"
              onClick={() => trackPhoneClick("header")}
            >
              <Button
                aria-label={`Call ${COMPANY.phone}`}
                className="bg-orange-700 hover:bg-orange-800 text-white font-bold uppercase tracking-wider text-xs h-11 px-3.5 2xl:px-5 shadow-md"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                <span className="hidden 2xl:inline ml-2">{COMPANY.phone}</span>
              </Button>
            </a>
          </div>

          {/* Mobile hamburger */}
          <button
            className="xl:hidden text-slate-800 p-2"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div
            className="xl:hidden border-t border-slate-200 bg-white max-h-[80vh] overflow-y-auto"
            data-testid="mobile-menu-panel"
          >
            <div className="px-4 py-3 flex flex-col gap-1">
              {NAV_MENU.map((item, idx) => {
                if (item.type === "link") {
                  return (
                    <Link
                      key={item.label}
                      to={item.href}
                      onClick={closeMobile}
                      data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="py-3 px-3 rounded-lg text-slate-800 hover:bg-slate-50 hover:text-orange-700 font-bold uppercase tracking-wider text-sm"
                    >
                      {item.label}
                    </Link>
                  );
                }
                const expanded = !!mobileExpanded[idx];
                return (
                  <div key={item.label} className="rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => toggleMobileSection(idx)}
                      aria-expanded={expanded}
                      data-testid={`mobile-nav-trigger-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className="w-full flex items-center justify-between py-3 px-3 rounded-lg text-slate-800 hover:bg-slate-50 hover:text-orange-700 font-bold uppercase tracking-wider text-sm"
                    >
                      <span>{item.label}</span>
                      <ChevronDown
                        className={`h-4 w-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                        aria-hidden="true"
                      />
                    </button>
                    {expanded && (
                      <ul
                        data-testid={`mobile-nav-dropdown-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        className="border-l-2 border-orange-100 ml-3 pl-2 my-1 flex flex-col gap-0.5"
                      >
                        {item.items.map((sub) => {
                          const href = resolveHref(sub.href);
                          const isHash = sub.href.includes("#");
                          const Cmp = isHash ? "a" : Link;
                          const linkProps = isHash
                            ? { href }
                            : { to: href };
                          return (
                            <li key={sub.href}>
                              <Cmp
                                {...linkProps}
                                onClick={closeMobile}
                                data-testid={`mobile-nav-item-${sub.href.replace(/[^a-z0-9]+/gi, "-")}`}
                                className="block py-2.5 px-3 rounded-md text-[13px] text-slate-700 hover:bg-orange-50 hover:text-orange-800"
                              >
                                <div className="font-semibold">{sub.label}</div>
                                {sub.desc && (
                                  <div className="text-[11px] text-slate-500 mt-0.5">
                                    {sub.desc}
                                  </div>
                                )}
                              </Cmp>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                );
              })}
              <Link
                to="/contact"
                onClick={closeMobile}
                data-testid="mobile-nav-link-contact"
                className="py-3 px-3 rounded-lg text-slate-800 hover:bg-slate-50 hover:text-orange-700 font-bold uppercase tracking-wider text-sm"
              >
                Contact Us
              </Link>
              <Link
                to="/careers"
                onClick={closeMobile}
                data-testid="mobile-nav-link-careers"
                className="py-3 px-3 rounded-lg text-slate-800 hover:bg-slate-50 hover:text-orange-700 font-bold uppercase tracking-wider text-sm"
              >
                Careers
              </Link>
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                onClick={() => { trackPhoneClick("mobile-menu"); closeMobile(); }}
                data-testid="mobile-call-cta"
                className="flex items-center justify-center gap-2 mt-3 py-3 bg-orange-700 text-white rounded-xl font-bold uppercase tracking-wider text-sm"
              >
                <Phone className="h-4 w-4" aria-hidden="true" />
                {COMPANY.phone}
              </a>
            </div>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
